import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild, ElementRef, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AppConfigService } from '@core/appconfig.service';
import { MessageEventService } from '@core/services/message-event/message-event.service';
import { PreloaderModule } from '@shared/preloader/preloader.module';

/**
 * Loads and automatically prints a PDF from a Blob using the PDF.js viewer.
 *
 * Can show spinner, waiting message, and error message if getting the Blob is not yet ready or errored.
 *
 * Loads the PDF in an iframe and sends a message through {@link MessageEventService}
 * to the iframe to trigger the print dialog.
 * The PDF Viewer listens for the print message and closes when printing is done.
 */
@Component({
  selector: 'zbp-pdf-auto-print',
  standalone: true,
  imports: [CommonModule, PreloaderModule,],
  templateUrl: './pdf-auto-print.component.html',
})
export class PdfAutoPrintComponent implements OnInit, OnChanges {
  /**
   * Blob of the PDF to be printed.
   *
   * Will be converted to a `SafeResourceUrl` for PDF.js viewer.
   */
  @Input() pdfBlob: Blob = null;
  @Input() waitingMessage: string = '';
  @Input() errorMessage: string = '';
  @Input() showSpinner: boolean = false;

  pdfJsSrc: SafeResourceUrl = undefined;

  @ViewChild('pdfIframe') pdfIframe!: ElementRef<HTMLIFrameElement>;

  constructor(
    private sanitizer: DomSanitizer,
    private appConfigService: AppConfigService,
    private messageEventService: MessageEventService,
  ) { }

  ngOnInit(): void {
    if (this.pdfBlob) {
      this.configureForPrint();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pdfBlob'] && this.pdfBlob) {
      this.configureForPrint();
    }
  }

  /** Configures the PDF.js viewer with the provided PDF blob and initiates the print process. */
  configureForPrint() {
    this.pdfJsSrc = this.sanitizer
      .bypassSecurityTrustResourceUrl(`${this.appConfigService.pdfViewerUrl}?file=${this.pdfBlob}`);

    // Give time for the PDF Viewer to load before triggering its printing function
    setTimeout(() => {
      this.autoPrintPdf();
    }, 2500);

    this.removeAllMessageForPrint();
  }

  /** Posts message to the PDF.js viewer iframe to print. */
  private autoPrintPdf(): void {
    if (this.pdfIframe?.nativeElement) {
      this.messageEventService.postMessageToPdfViewerToPrint(this.pdfIframe.nativeElement);
    }
  }

  private removeAllMessageForPrint(): void {
    this.waitingMessage = null;
    this.showSpinner = false;
  }
}
